import oasisDemoLarge from 'assets/oasis/oasis-demo-large.mp4';
import oasisDemoPlaceholder from 'assets/oasis/oasis-demo-placeholder.jpg';
import oasisDemo from 'assets/oasis/oasis-demo.mp4';
import oasisLogo from 'assets/oasis/oasis-logo.png';
import { Button } from 'components/Button';
import { Footer } from 'components/Footer';
import { Image } from 'components/Image';
import { Meta } from 'components/Meta';
import {
  ProjectContainer,
  ProjectHeader,
  ProjectSection,
  ProjectSectionContent,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectTextRow,
} from 'layouts/Project';
import { Fragment } from 'react';
import { media } from 'utils/style';
import styles from './oasis.module.css';

const title = 'Oasis';
const description =
  'Ask questions of your own documents with a language model that runs entirely on your machine. Oasis indexes PDFs, text and CSVs into a local Chroma database, retrieves the relevant passages, and answers offline.';
const roles = ['Solo Project', 'Python', 'LangChain + Chroma', 'Hugging Face'];

const ingestion = [
  { title: 'Files', detail: '.txt · .pdf · .csv' },
  { title: 'Chunks', detail: '1,000 characters · 200 overlap' },
  { title: 'Embeddings', detail: 'Instructor-XL' },
  { title: 'Index on disk', detail: 'Chroma' },
];

export function Oasis() {
  return (
    <Fragment>
      <Meta title={title} prefix="Projects" description={description} />
      <ProjectContainer className={styles.page}>
        <ProjectHeader
          title={title}
          logo={<img src={oasisLogo.src} width={oasisLogo.width} height={oasisLogo.height} alt="" />}
          description={description}
          url="https://github.com/txmxthy/OasisLLM"
          linkLabel="Source on GitHub"
          roles={roles}
        />
        <ProjectSection padding="top">
          <ProjectSectionContent>
            <figure className={styles.pipeline} aria-labelledby="oasis-pipeline-title">
              <figcaption id="oasis-pipeline-title" className={styles.figureTitle}>
                From source file to local index
              </figcaption>
              <ol className={styles.pipelineList}>
                {ingestion.map(({ title: step, detail }) => (
                  <li className={styles.pipelineStep} key={step}>
                    <span className={styles.stepTitle}>{step}</span>
                    <span className={styles.stepDetail}>{detail}</span>
                  </li>
                ))}
              </ol>
            </figure>
            <ProjectTextRow>
              <ProjectSectionHeading>Indexing the files</ProjectSectionHeading>
              <ProjectSectionText>
                Put files in the ingestion folder and run the ingestion command. LangChain
                splits them into overlapping chunks, Instructor-XL embeds the chunks, and
                Chroma stores the vectors on disk. I took the approach from PrivateGPT.
              </ProjectSectionText>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <Image
              raised
              className={styles.demo}
              srcSet={[
                { src: oasisDemo, width: 1280 },
                { src: oasisDemoLarge, width: 2560 },
              ]}
              placeholder={oasisDemoPlaceholder}
              alt="A PowerShell session in the Oasis toolkit: the menu, a GPU check finding an RTX 4090, then a question about Project Gutenberg answered from the ingested documents, with an offer to show the source passages."
              sizes={`(max-width: ${media.mobile}px) 100vw, 1000px`}
            />
            <div className={styles.queryLayout}>
              <ProjectTextRow width="s">
                <ProjectSectionHeading>Answering a question</ProjectSectionHeading>
                <ProjectSectionText>
                  The terminal menu in main.py starts a query session. Oasis finds nearby
                  chunks in Chroma, passes them to the local model as context, and prints
                  an answer. You can then ask to see the source passages before entering
                  another question.
                </ProjectSectionText>
                <ProjectSectionText>
                  The default model is Vicuna 7B through a Hugging Face text-generation
                  pipeline. Its temperature is set to zero. Once the models are downloaded,
                  queries use the index on disk and run offline.
                </ProjectSectionText>
              </ProjectTextRow>
              <figure className={styles.queryFigure} aria-labelledby="oasis-query-title">
                <figcaption id="oasis-query-title" className={styles.figureTitle}>
                  One query through Oasis
                </figcaption>
                <ol className={styles.queryList}>
                  <li><span>Question</span><strong>Entered in the terminal</strong></li>
                  <li><span>Retrieve</span><strong>Closest chunks from Chroma</strong></li>
                  <li><span>Generate</span><strong>Local model with retrieved context</strong></li>
                  <li><span>Inspect</span><strong>Answer and optional source passages</strong></li>
                </ol>
              </figure>
            </div>
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>Where it goes next</ProjectSectionHeading>
              <ProjectSectionText>
                Ingestion is manual today: changed files are copied in and ingested again.
                The next step is to automate that, so Oasis can follow a source, crawl a
                website or keep up with a project&rsquo;s documentation on its own.
              </ProjectSectionText>
              <ProjectSectionText>
                Running the model takes a capable GPU. I wrote and tested Oasis on an RTX
                4090 with 24 GB of VRAM, so the plan is to put it behind a chat app, where
                one machine answers for everyone who can message it. Microsoft Teams comes
                first, then Slack and Discord. Oasis is MIT-licensed and on GitHub.
              </ProjectSectionText>
              <Button secondary iconHoverShift icon="chevronRight" href="https://github.com/txmxthy/OasisLLM">
                Read the source
              </Button>
            </ProjectTextRow>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
}
